(() => {
  if (customElements.get('sm-cart-recommendations')) return;

  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.started) return;
      this.started = true;
      this.version = 0;
      this.cache = new Map();
      this.limit = Math.max(4, Math.min(6, Number(this.dataset.limit) || 6));
      this.cartIds = new Set(JSON.parse(this.dataset.cartProductIds || '[]').map(String));
      this.seedId = this.cartIds.values().next().value;
      this.signature = [...this.cartIds].sort().join(',');
      this.fallback = [...this.querySelector('template[data-sm-fallback]').content.children];
      this.onCartChange = () => {
        clearTimeout(this.cartTimer);
        this.cartTimer = setTimeout(() => this.refreshCart(), 250);
      };
      window.addEventListener('listCart', this.onCartChange);
      this.observer = new IntersectionObserver(entries => {
        if (!entries.some(entry => entry.isIntersecting)) return;
        this.observer.disconnect();
        this.active = true;
        this.update();
      }, { rootMargin: '300px' });
      this.observer.observe(this);
    }

    disconnectedCallback() {
      this.version++;
      this.observer?.disconnect();
      clearTimeout(this.cartTimer);
      window.removeEventListener('listCart', this.onCartChange);
      this.started = false;
    }

    recentProducts() {
      try {
        const saved = JSON.parse(localStorage.getItem('recentlyViewedProduct') || '[]');
        if (!Array.isArray(saved)) return [];
        const seen = new Set(this.cartIds);
        return saved.slice().reverse().filter(item => {
          if (!item || !/^\d+$/.test(String(item.productId)) || typeof item.productUrl !== 'string') return false;
          const id = String(item.productId);
          if (seen.has(id)) return false;
          seen.add(id);
          return true;
        }).slice(0, 4);
      } catch (_) {
        return [];
      }
    }

    productUrl(item) {
      const saved = new URL(item.productUrl, location.origin);
      const match = saved.pathname.match(/\/products\/([^/]+)\/?$/);
      if (!match) return null;
      const root = this.dataset.rootUrl.replace(/\/$/, '');
      const url = new URL(root + '/products/' + match[1], location.origin);
      url.searchParams.set('section_id', 'sm-cart-recommendations');
      return url;
    }

    async cardsFrom(url) {
      if (!url) return [];
      const key = String(url);
      if (!this.cache.has(key)) {
        this.cache.set(key, (async () => {
          const controller = new AbortController();
          const timer = setTimeout(() => controller.abort(), 8000);
          try {
            const response = await fetch(url, { signal: controller.signal });
            if (!response.ok) return [];
            const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
            return [...doc.querySelectorAll('[data-sm-recommendation-response] > [data-sm-recommendation-id]')];
          } catch (_) {
            return [];
          } finally {
            clearTimeout(timer);
          }
        })());
      }
      return this.cache.get(key);
    }

    chooseCards(groups) {
      const seen = new Set(this.cartIds);
      const cards = [];
      for (const group of groups) {
        for (const card of group) {
          const id = card.dataset.smRecommendationId;
          if (!id || seen.has(id)) continue;
          seen.add(id);
          cards.push(card.cloneNode(true));
          if (cards.length === this.limit) return cards;
        }
      }
      return cards;
    }

    async update() {
      const version = ++this.version;
      const viewed = this.recentProducts();
      const recentGroups = await Promise.all(viewed.map(item => {
        try { return this.cardsFrom(this.productUrl(item)); } catch (_) { return []; }
      }));
      let related = [];
      const seed = this.seedId || viewed[0]?.productId;
      if (seed && this.chooseCards(recentGroups).length < this.limit) {
        const url = new URL(this.dataset.recommendationsUrl, location.origin);
        url.searchParams.set('section_id', 'sm-cart-recommendations');
        url.searchParams.set('product_id', seed);
        url.searchParams.set('limit', '10');
        url.searchParams.set('intent', 'related');
        related = await this.cardsFrom(url);
      }
      if (!this.isConnected || version !== this.version) return;
      this.render(this.chooseCards([...recentGroups, related, this.fallback]));
    }

    render(cards) {
      const oldList = this.querySelector('.sm-cart-recommendation-list');
      const ids = cards.map(card => card.dataset.smRecommendationId).join(',');
      const currentIds = [...oldList.querySelectorAll('[data-sm-recommendation-id]')].map(card => card.dataset.smRecommendationId).join(',');
      this.hidden = cards.length === 0;
      if (ids === currentIds) return;
      const list = document.createElement('ul');
      list.className = 'l4cl w25 sm-cart-recommendation-list';
      list.append(...cards);
      oldList.replaceWith(list);
      for (const event of ['listScrollable', 'formZindex', 'semanticInput', 'semanticSelect', 'schemeTooltip', 'popups', 'ratings', 'productcardVariants']) {
        window.dispatchEvent(new CustomEvent(event));
      }
      if (typeof ajaxCart !== 'undefined') ajaxCart.init();
      if (typeof compareProducts !== 'undefined') compareProducts.initProducts();
      if (typeof quickShop !== 'undefined') quickShop.init();
      if (typeof window.check_limit_event === 'function') window.check_limit_event();
    }

    async refreshCart() {
      if (!this.isConnected || this.refreshing) return;
      this.refreshing = true;
      try {
        const response = await fetch(this.dataset.cartUrl);
        if (!response.ok) return;
        const cart = await response.json();
        if (!this.isConnected) return;
        const ids = new Set();
        for (const item of cart.items || []) {
          ids.add(String(item.product_id));
          for (const component of item.item_components || []) ids.add(String(component.product_id));
        }
        const signature = [...ids].sort().join(',');
        if (signature === this.signature) return;
        this.signature = signature;
        this.cartIds = ids;
        this.seedId = cart.items?.[0]?.product_id;
        this.version++;
        for (const card of this.querySelectorAll('.sm-cart-recommendation-list [data-sm-recommendation-id]')) {
          if (ids.has(card.dataset.smRecommendationId)) card.remove();
        }
        if (this.active) this.update();
      } catch (_) {
      } finally {
        this.refreshing = false;
      }
    }
  }

  customElements.define('sm-cart-recommendations', CartRecommendations);
})();
